import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Modal, Form, Select, Input, message, Upload, Mentions, Popover, Checkbox } from "antd";
import {
    useGetApplicationsQuery,
    useUpdateApplicationMutation,
    useAddApplicationMutation,
    useDeleteApplicationMutation
} from "@/redux/rtk/features/application/applicationApi";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import { useGetDealSuggestionsQuery, useGetDealsQuery } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import CreateDrawer from "@/components/CommonUi/CreateDrawer";
import { PlusOutlined, UploadOutlined, SettingOutlined, SearchOutlined, FilePdfOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import AutoComplete from "antd/es/auto-complete";
import Card from "@/UI/Card";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { addSingleAttachment } from "@/redux/rtk/features/CRM/attachment/attachmentSlice";
import TableComponent from "@/components/CommonUi/TableComponent";

const { Option } = Select;

import { getAttachmentUrl } from "@/utils/functions";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";
import CommonSearch from "@/components/CommonUi/CommonSearch";
import useApplicationFilter from "./useApplicationFilter";

export default function GetAllApplications({ isAgent = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [pageConfig, setPageConfig] = useState({
        page: 1,
        count: 10,
        status: "true",
    });
    const { data: applications, isLoading } = useGetApplicationsQuery(pageConfig);
    const { list: staffList } = useSelector((state) => state.users);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filters = useApplicationFilter(isFilterOpen, isAgent);

    const [statusForm] = Form.useForm();
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const [updateApplication, { isLoading: isUpdating }] = useUpdateApplicationMutation();
    const [addApplication, { isLoading: isAdding }] = useAddApplicationMutation();
    const [deleteApplication] = useDeleteApplicationMutation();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [addForm] = Form.useForm();

    const { data: deals, isLoading: dealsLoading } = useGetDealsQuery(isAgent ? { isAgent: true } : {});

    const { data: universitiesData, isLoading: uniLoading } = useGetUniversitiesQuery();
    const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();
    const universities = Array.isArray(universitiesData) ? universitiesData : (universitiesData?.universities ?? universitiesData?.getAllUniversity ?? []);

    const [visibleColumns, setVisibleColumns] = useState([
        "id", "dealName", "student", "agent", "university", "major", "language", "degree", "status", "note", "replay", "universityCode", "amount", "receipt", "paymentStatus", "createdAt", "updatedAt", "action"
    ]);
    const [search, setSearch] = useState("");

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            setPageConfig((prev) => ({
                ...prev,
                key: search,
                page: 1,
            }));
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (isStatusModalOpen) {
            dispatch(loadAllStaff({ query: "all" }));
        }
    }, [dispatch, isStatusModalOpen]);

    const statusOptions = [
        { label: t("application.statusOptions.submitted"), value: "Submitted", color: "blue" },
        { label: t("application.statusOptions.similar"), value: "Similar", color: "cyan" },
        { label: t("application.statusOptions.reject"), value: "Reject", color: "red" },
        { label: t("application.statusOptions.missDocument"), value: "Miss Document", color: "orange" },
        { label: t("application.statusOptions.cal"), value: "Cal", color: "purple" },
        { label: t("application.statusOptions.pending"), value: "pending", color: "default" },
    ];

    const paymentStatusOptions = [
        { label: t("application.paymentStatusOptions.pending"), value: "pending", color: "gold" },
        { label: t("application.paymentStatusOptions.paid"), value: "paid", color: "green" },
        { label: t("application.paymentStatusOptions.failed"), value: "failed", color: "red" },
        { label: t("application.paymentStatusOptions.refunded"), value: "refunded", color: "default" },
        { label: t("application.paymentStatusOptions.through"), value: "through", color: "cyan" },
        { label: t("application.paymentStatusOptions.normal"), value: "normal", color: "blue" },
    ];

    const handleOpenStatusModal = (record) => {
        setEditingApp(record);
        statusForm.setFieldsValue({
            status: record.status,
            note: record.note,
            replay: record.replay,
            university_code: record.universityCode,
            amount: record.amount,
            receipt: record.receipt,
            payment_status: record.paymentStatus || 'pending',
        });
        setIsStatusModalOpen(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t("application.deleteConfirm") || "Are you sure you want to delete this application?",
            okText: t("common.yes") || "Yes",
            okType: 'danger',
            cancelText: t("common.no") || "No",
            onOk: async () => {
                try {
                    await deleteApplication({ id }).unwrap();
                    message.success(t("application.successDelete") || "Application deleted successfully");
                } catch (error) {
                    message.error(t("application.errorDelete") || "Failed to delete application");
                }
            },
        });
    };

    const onStatusFinish = async (values) => {
        try {
            setIsProcessing(true);
            // Upload receipt if file is selected
            if (receiptFile) {
                const uniName = editingApp.university?.universityName || "";
                const attachmentCustomName = `${t("application.receipt")} ${uniName ? ` - ${uniName}` : ""}`;

                const formData = new FormData();
                formData.append('files[]', receiptFile);

                // Use correct keys and check for existence
                const leadId = editingApp.lead?.id || editingApp.leadId;
                const dealId = editingApp.deal?.id || editingApp.dealId;

                if (leadId) formData.append('leadId', leadId);
                if (dealId) formData.append('dealId', dealId);

                formData.append('attachmentType', 'receipt');
                formData.append('attachmentName', attachmentCustomName);

                const uploadResult = await dispatch(addSingleAttachment(formData)).unwrap();
                // successHandler returns { message: "success", data: { ... } }
                values.receipt = uploadResult.data?.attachmentPath || uploadResult.attachmentPath || receiptFile.name;
            }

            const resp = await updateApplication({
                id: editingApp.id,
                values: values,
            });
            if (resp.data) {
                message.success(t("application.successUpdate"));
                setIsStatusModalOpen(false);
                setEditingApp(null);
                setReceiptFile(null);
            }
        } catch (error) {
            console.error("Error updating application:", error);
            message.error(t("application.errorUpdate"));
        } finally {
            setIsProcessing(false);
        }
    };

    const onAddFinish = async (values) => {
        try {
            const resp = await addApplication(values);
            if (resp.data) {
                setIsAddModalOpen(false);
                addForm.resetFields();
                setSelectedUniversity(null);
            }
        } catch (error) {
            console.error("Error adding application:", error);
            message.error(t("application.errorAdd"));
        }
    };

    const handleUniversityChange = (value) => {
        const uni = universities.find((u) => u.id === value);
        setSelectedUniversity(uni);
        addForm.setFieldsValue({ major_id: null });
    };

    const allColumns = [
        {
            title: t("application.id"),
            dataIndex: "id",
            key: "id",
            width: 80,
            sorter: (a, b) => a.id - b.id,
            render: (id, record) => (
                <span
                    className="text-blue-500 cursor-pointer hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        isAgent ? navigate(`/agents/application/${record.id}`) : handleOpenStatusModal(record);
                    }}
                >
                    {id}
                </span>
            ),
        },
        {
            title: t("application.dealName"),
            dataIndex: "deal",
            key: "dealName",
            width: 150,
            ellipsis: true,
            sorter: (a, b) => (a.deal?.dealName || a.dealName || "").localeCompare(b.deal?.dealName || b.dealName || ""),
            render: (deal, record) => {
                const row = (record && typeof record === 'object') ? record : deal;
                const dName = row?.deal?.dealName || row?.dealName || "-";
                return isAgent ? (
                    <Link to={`/agents/application/${row?.id}`}>
                        {dName}
                    </Link>
                ) : (
                    <span className="text-blue-500 hover:underline">
                        {dName}
                    </span>
                );
            },
        },
        {
            title: t("application.student"),
            dataIndex: "deal",
            key: "student",
            width: 150,
            ellipsis: true,
            sorter: (a, b) => (a.deal?.dealName || a.dealName || "").localeCompare(b.deal?.dealName || b.dealName || ""),
            render: (deal, record) => {
                const row = (record && typeof record === 'object') ? record : deal;
                const d = row?.deal;
                const firstName = d?.firstName || row?.dealFirstName || row?.lead?.firstName || "-";
                const middleName = d?.middleName || row?.dealMiddleName || row?.lead?.middleName || "";
                const lastName = d?.lastName || row?.dealLastName || row?.lead?.lastName || "";
                const name = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
                return isAgent ? (
                    <Link to={`/agents/application/${row?.id}`}>
                        {name}
                    </Link>
                ) : (
                    <span className="text-blue-500 hover:underline">
                        {name}
                    </span>
                );
            },
        },
        {
            title: t("application.agent"),
            key: "agent",
            width: 150,
            ellipsis: true,
            render: (text, record) => {
                const row = (record && typeof record === 'object') ? record : text;
                if (row?.agentName && row?.agentName !== "-") return row.agentName;
                if (row?.agentFirstName) return `${row.agentFirstName} ${row.agentLastName || ""}`.trim();
                const agent = row?.deal?.agent || row?.lead?.agent;
                if (agent?.firstName) return `${agent.firstName} ${agent.lastName || ""}`.trim();
                return agent?.companyName || row?.agentCompanyName || "-";
            },
        },
        {
            title: t("application.leadSource"),
            key: "leadSource",
            width: 150,
            ellipsis: true,
            render: (text, record) => {
                const row = (record && typeof record === 'object') ? record : text;
                return row?.leadSourceName || row?.lead?.leadSource?.name || "-";
            },
        },
        {
            title: t("application.university"),
            dataIndex: "university",
            key: "university",
            width: 150,
            ellipsis: true,
            sorter: (a, b) => (a.university?.universityName || a.universityName || "").localeCompare(b.university?.universityName || b.universityName || ""),
            render: (uni, record) => {
                const row = (record && typeof record === 'object') ? record : uni;
                const u = row?.university;
                if (typeof u === 'string') return u;
                return u?.universityName || row?.universityName || "-";
            },
        },
        {
            title: t("application.major"),
            dataIndex: "major",
            key: "major",
            width: 150,
            ellipsis: true,
            sorter: (a, b) => (a.major?.name || a.majorName || "").localeCompare(b.major?.name || b.majorName || ""),
            render: (major, record) => {
                const row = (record && typeof record === 'object') ? record : major;
                const m = row?.major;
                if (typeof m === 'string') return m;
                return m?.name || row?.majorName || "-";
            },
        },
        {
            title: t("application.language"),
            dataIndex: "language",
            key: "language",
            width: 100,
            sorter: (a, b) => (a.language || "").localeCompare(b.language || ""),
            render: (lang) => <Tag color="blue">{lang?.toUpperCase()}</Tag>,
        },
        {
            title: t("application.degree") || "Degree",
            dataIndex: "degree",
            key: "degree",
            width: 120,
            sorter: (a, b) => (a.degree || "").localeCompare(b.degree || ""),
            render: (deg) => deg ? <Tag color="cyan">{deg?.toUpperCase()}</Tag> : "-",
        },

        {
            title: t("application.status"),
            dataIndex: "status",
            key: "status",
            width: 120,
            sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
            render: (status, record) => {
                const opt = statusOptions.find(o => o.value === status) || { label: status, color: "default" };
                return (
                    <Tag
                        color={opt.color}
                        className={!isAgent ? "cursor-pointer" : ""}
                        onClick={(e) => {
                            e.stopPropagation();
                            !isAgent && handleOpenStatusModal(record);
                        }}
                    >
                        {opt.label}
                    </Tag>
                );
            },
        },
        {
            title: t("application.note"),
            dataIndex: "note",
            key: "note",
            width: 150,
            sorter: (a, b) => (a.note || "").localeCompare(b.note || ""),
            render: (note) => <span className="text-gray-500 italic">{note || "-"}</span>
        },
        {
            title: t("application.universityReply"),
            dataIndex: "replay",
            key: "replay",
            width: 150,
            sorter: (a, b) => (a.replay || "").localeCompare(b.replay || ""),
            render: (replay) => <span className="text-gray-600">{replay || "-"}</span>
        },
        {
            title: t("application.universityCode"),
            dataIndex: "universityCode",
            key: "universityCode",
            sorter: (a, b) => (a.universityCode || "").localeCompare(b.universityCode || ""),
            render: (code) => code ? <Tag color="purple">{code}</Tag> : "-"
        },
        {
            title: t("application.amount"),
            dataIndex: "amount",
            key: "amount",
            sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
            render: (amount) => amount ? `$${parseFloat(amount).toFixed(2)}` : "-"
        },
        {
            title: t("application.receipt"),
            dataIndex: "receipt",
            key: "receipt",
            width: 100,
            render: (receipt) => receipt ? (
                <div className="flex items-center gap-2">
                    <a
                        href={getAttachmentUrl(receipt)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {t("application.view")}
                    </a>
                    <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            const url = getAttachmentUrl(receipt);
                            const link = document.createElement("a");
                            link.href = url;
                            link.target = "_blank";
                            link.download = "receipt";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                        title={t("attachment.download")}
                        className="flex items-center justify-center p-0 h-auto text-blue-500"
                    />
                </div>
            ) : "-"
        },
        {
            title: t("application.paymentStatus"),
            dataIndex: "paymentStatus",
            key: "paymentStatus",
            sorter: (a, b) => (a.paymentStatus || "").localeCompare(b.paymentStatus || ""),
            render: (status, record) => {
                const opt = paymentStatusOptions.find(o => o.value === status) || { label: status || t("application.statusOptions.pending"), color: "orange" };
                return (
                    <Tag
                        color={opt.color}
                        className={!isAgent ? "cursor-pointer" : ""}
                        onClick={(e) => {
                            e.stopPropagation();
                            !isAgent && handleOpenStatusModal(record);
                        }}
                    >
                        {opt.label?.toUpperCase()}
                    </Tag>
                );
            },
        },
        {
            title: t("application.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            render: (date) => new Date(date).toLocaleString(),
        },
        {
            title: t("common.updatedAt"),
            dataIndex: "updatedAt",
            key: "updatedAt",
            sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
            render: (date) => new Date(date).toLocaleString(),
        },
        {
            title: t("common.action") || "Action",
            key: "action",
            width: 80,
            render: (record) => [
                {
                    label: (
                        <div className="flex items-center gap-2">
                            <SettingOutlined />
                            <span>{t("common.edit") || "Edit"}</span>
                        </div>
                    ),
                    key: "edit",
                    onClick: (e) => {
                        e.stopPropagation();
                        handleOpenStatusModal(record);
                    },
                },
                {
                    label: (
                        <div className="flex items-center gap-2 text-red-600">
                            <DeleteOutlined />
                            <span>{t("common.delete") || "Delete"}</span>
                        </div>
                    ),
                    key: "delete",
                    onClick: (e) => {
                        e.stopPropagation();
                        handleDelete(record.id);
                    },
                },
            ],
        },
    ];

    const columns = allColumns.filter(column => visibleColumns.includes(column.key));

    const columnOptions = allColumns.map(col => ({
        label: col.title,
        value: col.key
    }));

    const columnContent = (
        <div className="max-h-64 overflow-y-auto p-2">
            <Checkbox.Group
                options={columnOptions}
                value={visibleColumns}
                onChange={(checkedValues) => setVisibleColumns(checkedValues)}
                className="flex flex-col gap-2"
            />
        </div>
    );

    return (
        <>
            <TableComponent
                list={applications?.getAllApplication}
                columns={columns}
                loading={isLoading}
                total={applications?.totalApplication}
                setPageConfig={setPageConfig}
                title={t("application.allApplications")}
                isSearch
                filters={filters}
                componentTitle={t("application.allApplications")}
                extraFilter={
                    <AdvancedFilter
                        page="application"
                        filters={filters}
                        pageConfig={pageConfig}
                        setPageConfig={setPageConfig}
                        onOpen={() => setIsFilterOpen(true)}
                    />
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsAddModalOpen(true)}
                    >
                        {t("application.addApplication") || "Add Application"}
                    </Button>
                }
                onRow={(record) => ({
                    onClick: () => {
                        if (isAgent) {
                            navigate(`/agents/application/${record.id}`);
                        } else {
                            handleOpenStatusModal(record);
                        }
                    },
                    className: 'cursor-pointer hover:bg-gray-50'
                })}
            />

            <Modal
                title={t("application.updateApplication")}
                open={isStatusModalOpen}
                onCancel={() => setIsStatusModalOpen(false)}
                footer={null}
                width={1400}
            >
                <Form form={statusForm} layout="vertical" onFinish={onStatusFinish}>
                    <Form.Item
                        name="status"
                        label={t("application.status")}
                        rules={[{ required: true }]}>
                        <Select placeholder={t("application.selectStatus")}>
                            {statusOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="replay"
                        label={t("application.universityReply")}>
                        <Input.TextArea
                            rows={2}
                            placeholder={t("application.enterUniversityReply")}
                        />
                    </Form.Item>

                    <Form.Item
                        name="university_code"
                        label={t("application.universityCodeLabel")}>
                        <Input placeholder={t("application.universityCodePlaceholder")} />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label={t("application.paymentAmount")}>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder={t("application.enterPaymentAmount")}
                            prefix="$"
                        />
                    </Form.Item>

                    <Form.Item
                        label={t("application.receiptUpload")}>
                        <Upload
                            maxCount={1}
                            beforeUpload={(file) => {
                                setReceiptFile(file);
                                return false; // Prevent auto upload
                            }}
                            onRemove={() => setReceiptFile(null)}
                        >
                            <Button icon={<UploadOutlined />}>{t("application.selectReceiptFile")}</Button>
                        </Upload>
                        {statusForm.getFieldValue('receipt') && (
                            <div className="mt-2">
                                <a 
                                    href={getAttachmentUrl(statusForm.getFieldValue('receipt'))} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:underline flex items-center gap-1"
                                >
                                    <FilePdfOutlined /> {t("application.viewCurrentReceipt")}
                                </a>
                            </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">{t("application.uploadReceiptHelp")}</div>
                    </Form.Item>

                    <Form.Item
                        name="payment_status"
                        label={t("application.paymentStatus")}>
                        <Select placeholder={t("application.selectPaymentStatus")}>
                            {paymentStatusOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label={t("application.noteLabel")}>
                        <Mentions
                            rows={4}
                            placeholder={t("application.notePlaceholder")}
                            options={staffList?.map(staff => ({
                                value: staff.username || `${staff.firstName}.${staff.lastName}`,
                                label: `${staff.firstName} ${staff.lastName}`,
                            }))}>
                        </Mentions>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isUpdating || isProcessing}
                            block>
                            {t("application.updateButton")}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t("application.addNewApplication") || "Add New Application"}
                open={isAddModalOpen}
                onCancel={() => {
                    setIsAddModalOpen(false);
                    addForm.resetFields();
                    setSelectedDeal(null);
                }}
                footer={null}
            >
                <Form form={addForm} layout="vertical" onFinish={onAddFinish}>
                    <Form.Item
                        label={t("application.dealName") || "Student/Deal"}
                        name="deal_id"
                        rules={[{ required: true, message: t("application.selectDeal") || "Please select a student" }]}
                    >
                        <Select
                            placeholder={t("application.selectDeal") || "Select a student"}
                            loading={dealsLoading}
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={deals?.map(d => ({
                                label: `${d.firstName} ${d.lastName} (${d.mobile || d.passportNumber || ""})`,
                                value: d.id,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="university_id"
                        label={t("application.university")}
                        rules={[{ required: true, message: t("application.selectUniversity") }]}>
                        <Select
                            placeholder={t("application.selectUniversity")}
                            onChange={handleUniversityChange}
                            loading={uniLoading}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }>
                            {universities.map((uni) => (
                                <Option key={uni.id} value={uni.id}>
                                    {uni.universityName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="major_id"
                        label={t("application.major")}
                        rules={[{ required: true, message: t("application.selectMajor") }]}>
                        <Select
                            placeholder={t("application.selectMajor")}
                            disabled={!selectedUniversity}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }>
                            {selectedUniversity?.majors?.map((major) => (
                                <Option key={major.id} value={major.id}>
                                    {major.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label={t("application.studyLanguage")}
                        rules={[{ required: true, message: t("lead.selectLanguage") }]}>
                        <Select placeholder={t("lead.selectLanguage")}>
                            <Option value="en">{t("lead.languages.en")}</Option>
                            <Option value="tr">{t("lead.languages.tr")}</Option>
                            <Option value="ar">{t("lead.languages.ar")}</Option>
                            <Option value="fr">{t("lead.languages.fr")}</Option>
                            <Option value="ru">{t("lead.languages.ru")}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="degree"
                        label={t("application.degreeUpdate")}
                        rules={[{ required: true, message: t("lead.selectDegree") }]}>
                        <Select
                            placeholder={t("lead.selectDegree")}
                            loading={degreeLoading}
                            showSearch
                        >
                            {degrees?.map((item) => (
                                <Option key={item.id} value={item.name}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isAdding}
                            block>
                            {t("application.submitApplication")}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );

}
